import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Copy, Loader2, Plus, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/hooks/use-toast';
import CreateBroadcastModal from '@/pages/Meta/Broadcast/components/CreateBroadcastModal';
import type { BroadcastFormData } from '@/pages/Meta/Broadcast/types/broadcast.types';
import { getActiveMetaPages } from '@/services/api';
import {
    atualizarRotina,
    criarRotinaGrupo,
    excluirRotina,
    excluirRotinaGrupo,
    getRotinaAgendamento,
    getRotinaDivergencia,
    grupoDoFormulario,
    mensagemDeErro,
    statusDeErro,
    updateRotinaGrupo,
    type Rotina,
    type RotinaGrupo,
    type RotinaGrupoCriado,
    type RotinaGrupoPayload,
    type RotinaGrupoSalvo,
    type RotinaGrupoUpdate,
} from '@/services/api/rotinas';
import DuplicarBroadcastDialog from './DuplicarBroadcastDialog';
import GruposTabela from './GruposTabela';
import LinhaDoTempo from './LinhaDoTempo';
import PainelLateral from './PainelLateral';
import RenomearDialog from './RenomearDialog';
import RotinaCabecalho from './RotinaCabecalho';
import ConflitosDialog from './ConflitosDialog';
import CategoriasDialog from './CategoriasDialog';
import type { Categoria } from './CategoriasDoGrupo';
import { ResultadoDaCriacao } from './ResultadosDoGrupo';
import { FORMULARIO_VAZIO, formularioDoGrupo, soOQueMudou, type Editor } from './grupoEditor';
import {
    HORA_MATERIALIZACAO, hhmm, hojeBRT, horaAgoraBRT, ordenarGrupos, temTituloComum,
} from './rotinaUtils';

export default function RotinaCard({ rotina, outraNoAr, somenteLeitura }: {
    rotina: Rotina;
    /** Outra Rotina do mesmo dia que também está no ar — as duas convivem. */
    outraNoAr?: Rotina;
    /** Admin vendo Rotina de outro usuário: por ora só vê, o backend recusa a escrita. */
    somenteLeitura?: boolean;
}) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const completa = useQuery({
        queryKey: ['rotina-agendamento', rotina.id],
        queryFn: () => getRotinaAgendamento(rotina.id),
    });
    const seletor = useQuery({
        // Sem staleTime longo: esta lista filtra a contagem de paginas de cada
        // grupo, entao 5 min de cache faziam a tela negar por minutos uma
        // atualizacao que ja estava no banco.
        queryKey: ['active-pages'],
        queryFn: getActiveMetaPages,
    });
    const divergencia = useQuery({
        queryKey: ['rotina-agendamento-divergencia', rotina.id],
        queryFn: () => getRotinaDivergencia(rotina.id),
    });

    const [editor, setEditor] = useState<Editor | null>(null);
    const [duplicando, setDuplicando] = useState(false);
    const [renomeando, setRenomeando] = useState(false);
    const [porCategoria, setPorCategoria] = useState(false);
    const [confirmaHora, setConfirmaHora] = useState<RotinaGrupoPayload | null>(null);
    const [apagarGrupo, setApagarGrupo] = useState<RotinaGrupo | null>(null);
    const [apagarRotina, setApagarRotina] = useState(false);
    const [criado, setCriado] = useState<RotinaGrupoCriado | null>(null);
    // Erros de disparo só saem no Dispensar: um sucesso posterior não pode escondê-los.
    const [comErro, setComErro] = useState<RotinaGrupoCriado[]>([]);
    const [salvos, setSalvos] = useState<Record<number, RotinaGrupoSalvo>>({});
    const [erroLigar, setErroLigar] = useState<{ titulo: string; texto: string } | null>(null);
    const [verConflitos, setVerConflitos] = useState(false);

    const recarregar = () => {
        queryClient.invalidateQueries({ queryKey: ['rotinas-agendamento'] });
        queryClient.invalidateQueries({ queryKey: ['rotina-agendamento', rotina.id] });
        queryClient.invalidateQueries({ queryKey: ['rotina-agendamento-divergencia', rotina.id] });
        // 'active-pages' tambem: a contagem de cada grupo e o cruzamento do molde
        // com esta lista (ver `grupos` abaixo). Sem invalidar, a tela mostra o
        // numero velho depois de atualizar por categoria e parece que nao funcionou.
        queryClient.invalidateQueries({ queryKey: ['active-pages'] });
    };

    const criar = useMutation({
        mutationFn: (p: RotinaGrupoPayload) => criarRotinaGrupo(rotina.id, p),
        onSuccess: (r) => {
            if (r.erro_disparo_hoje) setComErro((l) => [...l, r]);
            else setCriado(r);
            setEditor(null);
            setConfirmaHora(null);
            recarregar();
            if (r.erro_disparo_hoje) {
                toast({
                    variant: 'destructive',
                    title: 'Grupo salvo, mas os disparos de hoje NÃO foram criados',
                    description: 'Confira antes de recriar.',
                });
            } else {
                toast({ title: 'Grupo salvo' });
            }
        },
        onError: (e) => {
            setConfirmaHora(null);
            toast({ variant: 'destructive', title: 'Não foi possível criar o grupo', description: mensagemDeErro(e, 'Tente de novo.') });
        },
    });

    const editar = useMutation({
        mutationFn: ({ grupo, dados }: { grupo: RotinaGrupo; dados: RotinaGrupoUpdate }) =>
            updateRotinaGrupo(rotina.id, grupo.id, dados),
        onSuccess: (r, { grupo }) => {
            setSalvos((s) => ({ ...s, [grupo.id]: r }));
            setEditor(null);
            setConfirmaHora(null);
            recarregar();
            toast({ title: 'Grupo salvo' });
        },
        onError: (e) => {
            setConfirmaHora(null);
            toast({ variant: 'destructive', title: 'Não foi possível salvar', description: mensagemDeErro(e, 'Tente de novo.') });
        },
    });

    const ligar = useMutation({
        mutationFn: (ativa: boolean) => atualizarRotina(rotina.id, { ativa }),
        onSuccess: () => {
            setErroLigar(null);
            recarregar();
        },
        onError: (e) => {
            setErroLigar({
                titulo: 'Não foi possível ligar esta Rotina',
                texto: statusDeErro(e) === 409
                    ? mensagemDeErro(e, 'Já existe outra Rotina no ar neste dia. Desligue-a antes de ligar esta.')
                    : mensagemDeErro(e, 'Não foi possível alterar a Rotina.'),
            });
        },
    });

    /**
     * Ligar esta tirando a outra do ar. Dois PUTs, nesta ordem: ligar primeiro
     * bateria no 409. Se o segundo falhar o dia fica SEM Rotina no ar, e isso
     * tem de ser dito com todas as letras.
     */
    const apagarGrupoM = useMutation({
        mutationFn: (g: RotinaGrupo) => excluirRotinaGrupo(rotina.id, g.id),
        onSuccess: (r) => {
            setApagarGrupo(null);
            recarregar();
            toast({
                title: 'Grupo apagado',
                description: `${r.broadcasts_desvinculados} disparo(s) já agendado(s) continuam na fila.`,
            });
        },
        onError: (e) => toast({ variant: 'destructive', title: 'Não foi possível apagar', description: mensagemDeErro(e, 'Tente de novo.') }),
    });

    const apagarRotinaM = useMutation({
        mutationFn: () => excluirRotina(rotina.id),
        onSuccess: (r) => {
            setApagarRotina(false);
            queryClient.removeQueries({ queryKey: ['rotina-agendamento', rotina.id] });
            queryClient.removeQueries({ queryKey: ['rotina-agendamento-divergencia', rotina.id] });
            queryClient.invalidateQueries({ queryKey: ['rotinas-agendamento'] });
            toast({
                title: 'Rotina apagada',
                description: `${r.grupos_apagados} grupo(s) apagado(s). ${r.broadcasts_desvinculados} disparo(s) já agendado(s) continuam na fila.`,
            });
        },
        onError: (e) => toast({ variant: 'destructive', title: 'Não foi possível apagar', description: mensagemDeErro(e, 'Tente de novo.') }),
    });

    const ligarGrupo = useMutation({
        mutationFn: ({ grupo, ativa }: { grupo: RotinaGrupo; ativa: boolean }) =>
            updateRotinaGrupo(rotina.id, grupo.id, { ativa }),
        onSuccess: (r, { grupo }) => {
            setSalvos((s) => ({ ...s, [grupo.id]: r }));
            recarregar();
        },
        onError: (e) => toast({ variant: 'destructive', title: 'Não foi possível alterar o grupo', description: mensagemDeErro(e, 'Tente de novo.') }),
    });

    /** Um PUT só de `paginas` por grupo: não propaga nada aos broadcasts já criados. */
    const tirarDoMolde = useMutation({
        mutationFn: async (pageIds: string[]) => {
            const fora = new Set(pageIds);
            const alvo = (completa.data?.grupos ?? []).filter((g) => g.paginas.some((p) => fora.has(p)));
            for (const g of alvo) {
                await updateRotinaGrupo(rotina.id, g.id, { paginas: g.paginas.filter((p) => !fora.has(p)) });
            }
            return alvo.length;
        },
        onSuccess: (n, pageIds) => {
            toast({ title: `${pageIds.length} página(s) tirada(s) do molde`, description: `${n} grupo(s) atualizado(s).` });
            recarregar();
        },
        onError: (e) => {
            toast({ variant: 'destructive', title: 'Não foi possível tirar todas do molde', description: mensagemDeErro(e, 'Tente de novo.') });
            recarregar();
        },
    });

    /** Abre com o grupo do MOLDE, não o filtrado da tela, para o salvar preservar as pausadas. */
    const abrirEditar = async (visivel: RotinaGrupo) => {
        const grupo = completa.data?.grupos.find((g) => g.id === visivel.id) ?? visivel;
        try {
            const ativas = await queryClient.fetchQuery({
                queryKey: ['active-pages'],
                queryFn: getActiveMetaPages,
            });
            const porPageId = new Map(ativas.map((p) => [p.page_id, p]));
            const selectedPages = grupo.paginas.flatMap((id) => {
                const p = porPageId.get(id);
                return p ? [{ id: p.id, page_id: p.page_id, page_name: p.page_name }] : [];
            });
            const foraDoSeletor = grupo.paginas.filter((id) => !porPageId.has(id));
            setEditor({ grupo, foraDoSeletor, initialData: formularioDoGrupo(grupo, selectedPages) });
        } catch {
            toast({ variant: 'destructive', title: 'Não consegui carregar as páginas', description: 'Tente de novo.' });
        }
    };

    /** Hoje é o dia da Rotina, ela está ligada e a hora já passou: o disparo de hoje sai na hora. */
    const horaVencida = (hora: string) =>
        !!rotina.ativa && rotina.dia_semana === hojeBRT().getDay() && hora <= horaAgoraBRT();

    const enviar = (payload: RotinaGrupoPayload) => {
        if (!editor) return;
        if (editor.grupo) {
            const dados = soOQueMudou(editor.grupo, payload);
            if (Object.keys(dados).length === 0) {
                setEditor(null);
                toast({ title: 'Nada mudou' });
                return;
            }
            editar.mutate({ grupo: editor.grupo, dados });
        } else {
            criar.mutate(payload);
        }
    };

    const aoConfirmarModal = (data: BroadcastFormData) => {
        if (!editor) return;
        const base = grupoDoFormulario(data);
        if (!base.titulo_modelo) {
            toast({ variant: 'destructive', title: 'Dê um título ao grupo' });
            return;
        }
        if (data.broadcastType === 'esteira') {
            if (!base.utility_esteira_id) {
                toast({ variant: 'destructive', title: 'Selecione uma Esteira.' });
                return;
            }
        } else if (data.broadcastType === 'utility') {
            if (!base.utility_template_id) {
                toast({ variant: 'destructive', title: 'Selecione um template utility.' });
                return;
            }
        } else if (base.paginas.length === 0) {
            // Esteira traz as próprias páginas e utility resolve o template por
            // página: só standard e tag dependem da lista do grupo.
            toast({ variant: 'destructive', title: 'Selecione pelo menos uma página.' });
            return;
        }
        const payload = { ...base, paginas: [...base.paginas, ...editor.foraDoSeletor] };
        const horaMudou = !editor.grupo || payload.hora_local !== hhmm(editor.grupo.hora_local);
        if (horaMudou && horaVencida(payload.hora_local)) {
            setConfirmaHora(payload);
            return;
        }
        enviar(payload);
    };

    const d = divergencia.data;
    // A tela mostra de cada grupo só o que o seletor de páginas mostra: página
    // pausada ou fora da conta não aparece aqui nem dispara.
    const grupos = useMemo(() => {
        if (!completa.data || !seletor.data) return [];
        const visiveis = new Set(seletor.data.map((p) => p.page_id));
        return ordenarGrupos(completa.data.grupos).map((g) => ({
            ...g, paginas: g.paginas.filter((id) => visiveis.has(id)),
        }));
    }, [completa.data, seletor.data]);
    const tituloComum = temTituloComum(grupos);
    const categorias = useMemo(() => {
        const m = new Map<number, Categoria>();
        for (const p of seletor.data ?? []) {
            if (p.internal_category_id == null) continue;
            const c = m.get(p.internal_category_id)
                ?? { id: p.internal_category_id, nome: p.internal_category_name ?? 'Sem nome', paginas: new Set<string>() };
            c.paginas.add(p.page_id);
            m.set(c.id, c);
        }
        return [...m.values()];
    }, [seletor.data]);

    const noAr = !!rotina.ativa;
    const ocupadoLigar = ligar.isPending;

    return (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
            <article className="overflow-hidden rounded-xl border bg-card">
                <RotinaCabecalho
                    rotina={rotina}
                    grupos={grupos}
                    tituloComum={tituloComum}
                    ultimaExecucao={completa.data ? completa.data.ultima_execucao : rotina.ultima_execucao}
                    ocupado={ocupadoLigar}
                    onRenomear={() => setRenomeando(true)}
                    onApagar={() => setApagarRotina(true)}
                    // Ligar não tira mais ninguém do ar: várias Rotinas convivem
                    // no mesmo dia (separar por região, por exemplo). O que o
                    // backend recusa é dividir PÁGINA com outra ligada, e aí vem
                    // um 409 nomeando a Rotina e as páginas.
                    onAlternar={() => ligar.mutate(!noAr)}
                    somenteLeitura={somenteLeitura}
                />

                {somenteLeitura && (
                    <p className="mx-4 mb-3 rounded-lg bg-slate-100 px-3 py-2 text-[13px] text-slate-700">
                        Rotina de <strong>{rotina.owner_name || `Usuário ${rotina.user_id}`}</strong>: você está vendo, sem editar.
                    </p>
                )}

                {/* Dito ANTES de ligar, não como 409 depois. */}
                {!noAr && outraNoAr && (
                    <p className="mx-4 mb-3 rounded-lg bg-sky-50 px-3 py-2 text-[13px] text-sky-900">
                        <strong>{outraNoAr.nome}</strong> também está no ar neste dia e continua — as duas
                        materializam. Só não pode haver a mesma página nas duas, senão o lead receberia
                        dois disparos.
                    </p>
                )}
                {!noAr && !outraNoAr && (
                    <p className="mx-4 mb-3 text-[13px] text-muted-foreground">
                        Desligada: não materializa nada às {HORA_MATERIALIZACAO}.
                    </p>
                )}

                {erroLigar && (
                    <div className="mx-4 mb-3">
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle>{erroLigar.titulo}</AlertTitle>
                            <AlertDescription>
                                {erroLigar.texto}{' '}
                                {/* A mensagem do backend cabe três page_id; quem tem 50 em
                                    conflito precisa da lista inteira, com nome, para agir. */}
                                <button
                                    type="button"
                                    className="font-semibold underline underline-offset-2"
                                    onClick={() => setVerConflitos(true)}
                                >
                                    Ver todas as páginas
                                </button>
                                {' · '}
                                <button type="button" className="underline underline-offset-2" onClick={() => setErroLigar(null)}>
                                    Dispensar
                                </button>
                            </AlertDescription>
                        </Alert>
                    </div>
                )}

                {grupos.length > 0 && (
                    <div className="flex flex-col gap-2 border-t p-4">
                        <div className="flex justify-between text-[13px] text-muted-foreground">
                            <span title="Cada grupo dispara na hora local da sua página; a régua mostra onde isso cai em Brasília.">
                                Disparos ao longo do dia (horário de Brasília)
                            </span>
                            <span className="tabular-nums">
                                <strong className="text-foreground">{grupos.filter((g) => g.ativa).length}</strong> de{' '}
                                <strong className="text-foreground">{grupos.length}</strong> grupos ativos
                            </span>
                        </div>
                        <LinhaDoTempo grupos={grupos} />
                    </div>
                )}

                {(completa.isLoading || seletor.isLoading) && (
                    <p className="flex items-center gap-2 border-t p-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Carregando os grupos…
                    </p>
                )}
                {(completa.isError || seletor.isError) && (
                    <p className="border-t p-4 text-sm text-destructive">Não foi possível carregar os grupos.</p>
                )}
                {completa.data && completa.data.grupos.length === 0 && (
                    <p className="border-t p-4 text-sm text-muted-foreground">
                        Rotina sem grupos: não há o que materializar. Use "Novo grupo" abaixo.
                    </p>
                )}

                {grupos.length > 0 && (
                    <GruposTabela
                        grupos={grupos}
                        diaSemana={rotina.dia_semana}
                        tituloComum={tituloComum}
                        categorias={categorias}
                        ocupado={ligarGrupo.isPending || apagarGrupoM.isPending}
                        salvos={salvos}
                        onEditar={abrirEditar}
                        onApagar={setApagarGrupo}
                        onLigar={(grupo, ativa) => ligarGrupo.mutate({ grupo, ativa })}
                        onDispensar={(g) => setSalvos(({ [g.id]: _descartado, ...resto }) => resto)}
                        somenteLeitura={somenteLeitura}
                    />
                )}

                {(comErro.length > 0 || criado) && (
                    <div className="space-y-2 border-t p-4">
                        {comErro.map((r) => (
                            <ResultadoDaCriacao key={r.id} r={r} onFechar={() => setComErro((l) => l.filter((x) => x.id !== r.id))} />
                        ))}
                        {criado && <ResultadoDaCriacao r={criado} onFechar={() => setCriado(null)} />}
                    </div>
                )}

                {!somenteLeitura && (
                <div className="flex flex-wrap gap-2 border-t bg-muted/30 p-3">
                    <Button size="sm" variant="outline" onClick={() => setEditor({ initialData: FORMULARIO_VAZIO, foraDoSeletor: [] })}>
                        <Plus className="mr-1 h-4 w-4" /> Novo grupo
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDuplicando(true)}>
                        <Copy className="mr-1 h-4 w-4" /> Grupo a partir de um broadcast
                    </Button>
                    {grupos.length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => setPorCategoria(true)}>
                            <RefreshCw className="mr-1 h-4 w-4" /> Atualizar pelas categorias
                        </Button>
                    )}
                </div>
                )}
            </article>

            <PainelLateral
                grupos={grupos}
                divergencia={d}
                divergenciaComErro={divergencia.isError}
                onTirarDoMolde={(ids) => tirarDoMolde.mutate(ids)}
                tirando={tirarDoMolde.isPending}
                somenteLeitura={somenteLeitura}
            />

            <RenomearDialog rotina={rotina} open={renomeando} onOpenChange={setRenomeando} />

            <DuplicarBroadcastDialog
                open={duplicando}
                onOpenChange={setDuplicando}
                onEscolher={(initialData) => {
                    setDuplicando(false);
                    setEditor({ initialData, foraDoSeletor: [] });
                }}
            />

            <ConflitosDialog rotinaId={rotina.id} open={verConflitos} onOpenChange={setVerConflitos} />

            <CategoriasDialog
                rotinaId={rotina.id}
                grupos={grupos}
                open={porCategoria}
                onOpenChange={setPorCategoria}
                onAtualizado={recarregar}
            />

            {editor && (
                <CreateBroadcastModal
                    open
                    onOpenChange={(aberto) => { if (!aberto) setEditor(null); }}
                    onSubmit={aoConfirmarModal}
                    isLoading={criar.isPending || editar.isPending}
                    initialData={editor.initialData}
                    isEditMode={!!editor.grupo}
                    modoRotina={{ diaSemana: rotina.dia_semana }}
                />
            )}

            <ConfirmDialog
                open={!!confirmaHora}
                onOpenChange={(aberto) => { if (!aberto) setConfirmaHora(null); }}
                variant="warning"
                title="Essa hora já passou em Brasília"
                description={`Agora são ${horaAgoraBRT()} e hoje é o dia desta Rotina. Os disparos de hoje deste grupo vão sair imediatamente.`}
                confirmLabel="Salvar mesmo assim"
                isLoading={criar.isPending || editar.isPending}
                onConfirm={() => { if (confirmaHora) enviar(confirmaHora); }}
            />

            <ConfirmDialog
                open={!!apagarGrupo}
                onOpenChange={(aberto) => { if (!aberto) setApagarGrupo(null); }}
                variant="danger"
                title={apagarGrupo ? `Apagar o grupo das ${hhmm(apagarGrupo.hora_local)}?` : 'Apagar grupo?'}
                description="Apaga só o molde: ele deixa de materializar nas próximas semanas. Os disparos já agendados com ele continuam na fila, apagar não cancela nada. Para cancelar, use a tela de Broadcasts."
                confirmLabel="Apagar grupo"
                isLoading={apagarGrupoM.isPending}
                onConfirm={() => { if (apagarGrupo) apagarGrupoM.mutate(apagarGrupo); }}
            />

            <ConfirmDialog
                open={apagarRotina}
                onOpenChange={setApagarRotina}
                variant="danger"
                title={`Apagar a Rotina "${rotina.nome}"?`}
                description="Apaga a Rotina e todos os grupos dela: nada mais materializa. Os disparos já agendados continuam na fila, apagar não cancela nada. Para cancelar, use a tela de Broadcasts."
                confirmLabel="Apagar Rotina"
                isLoading={apagarRotinaM.isPending}
                onConfirm={() => apagarRotinaM.mutate()}
            />
        </div>
    );
}
